/**
 * 把库里的菜单注册成真实路由。
 *
 * ## 组件路径失去了编译期检查
 *
 * 菜单进库之后，`component` 是一个字符串（`views/job/JobListView.vue`）。
 * 打包器不可能按运行时才知道的字符串去加载文件，所以这里用
 * `import.meta.glob` 把 `views/` 下所有 `.vue` **预先**登记成一张
 * 「路径 → 动态 import」的表，再按名字查。
 *
 * 代价是路径写错不会有任何编译错误。所以查不到时**必须喊出来** ——
 * 静默跳过的话，那条菜单会照常出现在侧栏，点进去是一片空白，
 * 而控制台什么都没有。
 *
 * ## 注册完必须重试当前导航
 *
 * `router.addRoute()` 不会重新解析正在进行的这次导航。守卫里加完路由就
 * `return true` 的话，用户刷新一个动态页面会命中 404 —— 而再点一次就好了。
 * 这种「第一次不行第二次行」的毛病最难查，所以这里返回是否真的加了路由，
 * 由调用方决定要不要重试导航。
 */
import type { RouteRecordSingleView, Router } from 'vue-router'

import { flatten, type MenuNode } from '@/api/menus'

/**
 * `views/` 下的全部页面组件。
 *
 * 键形如 `/src/views/job/JobListView.vue`；库里存的是 `views/job/JobListView.vue`，
 * 所以查表时补上 `/src/` 前缀。
 */
const MODULES = import.meta.glob('/src/views/**/*.vue')

/** 动态路由都挂在这个父路由下，与静态菜单共用同一套布局。 */
const PARENT_ROUTE_NAME = 'root'

export interface RegisterResult {
  /** 真正新加的路由数。0 表示这次没动路由表，调用方不必重试导航。 */
  added: number
  /** 组件路径在 `views/` 下查不到的菜单。交给调用方报错，不在这里吞掉。
   *  带上 `showStatus`：已隐藏的坏菜单不在侧栏里，「点得开但空白」对它不成立，
   *  调用方据此把 error 降为 warn。 */
  missing: { menuName: string; component: string; showStatus: string }[]
}

function loaderFor(component: string): (() => Promise<unknown>) | undefined {
  return MODULES[`/src/${component}`]
}

/**
 * 按菜单树注册路由。
 *
 * **已存在同名路由的直接跳过**，不覆盖：静态路由表里那些带参数的详情页
 * （`jobs/:id`、`models/:id`）不在菜单里，但它们的列表页在。用库里的版本
 * 覆盖静态定义，会把 `meta.parent`、`keepAlive` 这些菜单表里根本没有的字段抹掉。
 */
export function registerMenuRoutes(router: Router, tree: readonly MenuNode[]): RegisterResult {
  const result: RegisterResult = { added: 0, missing: [] }

  for (const node of flatten(tree)) {
    // 目录只是分组，没有页面
    if (node.menu_type !== '2' || !node.route_name || !node.route_path || !node.component) continue
    if (router.hasRoute(node.route_name)) continue

    const loader = loaderFor(node.component)
    if (!loader) {
      result.missing.push({
        menuName: node.menu_name,
        component: node.component,
        showStatus: node.show_status,
      })
      continue
    }

    // 用 `RouteRecordSingleView` 而不是 `RouteRecordRaw`：后者是个联合类型，
    // TS 会挑中要求 `redirect` 的那一支然后报「缺少 redirect」。
    const record: RouteRecordSingleView = {
      path: node.route_path,
      name: node.route_name,
      component: loader as RouteRecordSingleView['component'],
      meta: {
        title: node.menu_name,
        ...(node.perm_code ? { perms: [node.perm_code] } : {}),
      },
    }
    router.addRoute(PARENT_ROUTE_NAME, record)
    result.added += 1
  }

  return result
}
